import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./AppShell";
import { StaffView } from "./components/staff/StaffView";
import { BrandMark } from "./components/common";
import { apiUrl, LOGIN_PATH, MICROSOFT_LOGIN_PATH } from "./lib/api";
import { recordLogin, loginLabelFor } from "./lib/activity";
import { pathToView } from "./pages/routeMap";
import { STAFF_PAGE_LABELS, type Me as ApiMe } from "@bbbk/schema";
import { CustomerHome, JoinPage, customerRoute } from "./customer/CustomerApp";
import { CustomerSignIn } from "./customer/CustomerSignIn";
import { CUSTOMER_HOME, CUSTOMER_REGISTER, CUSTOMER_BOOK, CUSTOMER_DOGS, CUSTOMER_PAY, STAFF_PATH, landingFor } from "./customer/landing";

// The GET /api/me session shape is single-sourced in @bbbk/schema. The web
// widens role to allow a null "unknown / not-yet-resolved" state the shell
// renders as "Staff"; the wire always carries a concrete Role, so the null is a
// UI-only widening — the rest of the shape comes straight from schema.
type Me = Omit<ApiMe, "role"> & { role: ApiMe["role"] | null };

type Auth =
  | { state: "loading" }
  | { state: "signed-out" }
  | { state: "signed-in"; me: Me }
  | { state: "error"; message: string };

export function App() {
  const [auth, setAuth] = useState<Auth>({ state: "loading" });

  // Warm-up on app boot — fires GET /api/health in parallel so Neon
  // compute is warm by the time the bookings fetch lands. Fire-and-forget: no
  // envelope, errors swallowed (AC5), so it stays a bare fetch — only the base
  // path is single-sourced via apiUrl. See docs/decision/performance-budget.md.
  useEffect(() => {
    fetch(apiUrl("/health")).catch(() => {
      /* warm-up only; failure surfaces on the real bookings fetch */
    });
  }, []);

  // Session probe — 401 means signed out (or session expired), NOT a redirect:
  // this 401 drives the sign-in button, so it must not route through the
  // client's redirect-on-401 (AC5). The /api/me body is the bare Me object, not
  // an enveloped { data }, so this stays a bare fetch using the single-sourced
  // base path. See docs/decision/auth-google-sso.md.
  useEffect(() => {
    fetch(apiUrl("/me"))
      .then(async (r) => {
        if (r.status === 401) {
          setAuth({ state: "signed-out" });
          return;
        }
        if (!r.ok) {
          setAuth({ state: "error", message: `HTTP ${r.status}` });
          return;
        }
        const me = (await r.json()) as Me;
        setAuth({ state: "signed-in", me });
        // Admin metrics — once per tab session (guarded). The landing page is
        // recorded HERE because nothing downstream can recover it: a page view
        // is written only on LEAVING a page, so a session that opens somewhere
        // and stays put never produces one. A staff user lands on their own
        // self-view, which opens on the daily briefing for today.
        recordLogin(loginLabelFor(me.role, window.location.pathname, pathToView, STAFF_PAGE_LABELS.today), me.email);
      })
      .catch((e: Error) => setAuth({ state: "error", message: e.message }));
  }, []);

  const path = window.location.pathname;
  // THE CUSTOMER'S DOOR IS ANSWERED BEFORE THE SESSION PROBE RESOLVES, and
  // before anything else on this page. Somebody opening a registration link has
  // no session and is not supposed to have one yet — waiting on /api/me would
  // show them a blank screen first and then, briefly, the signed-out desk
  // landing, which is a page about a kennel management system they will never
  // use. Home is here too rather than under the shell: a `customer` role
  // reaches no desk route (visibleNav gives it nothing), so mounting AppShell
  // for one would render an app with no navigation at all.
  const customer = customerRoute(path);
  if (customer?.kind === "join") return <JoinPage token={customer.token} />;
  if (path === "/denied") return <DeniedPage />;
  // The OAuth callback bounces a soft state failure here (after a guarded
  // auto-retry) instead of dead-ending on raw JSON. Static page — no session
  // needed, so it renders before the auth probe resolves.
  if (path === "/signin-error") return <SigninError />;

  // The customer sign-in page answers before the session probe resolves:
  // somebody following a spent link has no session and is not waiting to find
  // that out. Once the probe HAS answered, a signed-in person is routed by
  // their role instead — offering sign-in to somebody signed in is one of the
  // in-between states this app does not have.
  if (customer?.kind === "signin" && auth.state !== "signed-in") {
    return <CustomerSignIn refused={new URLSearchParams(window.location.search).get("refused")} />;
  }

  if (auth.state === "loading") {
    return <main className="page-main" />;
  }
  // A SIGNED-OUT BROWSER GETS THE DOOR THAT MATCHES THE URL IT ASKED FOR. The
  // desk's own signed-out card says "sign in with your kennel Google or
  // Microsoft account", which is the wrong sentence entirely for a customer
  // whose session expired while they were looking at their stays.
  if (auth.state === "signed-out") {
    return customer ? <CustomerSignIn /> : <SignedOut />;
  }
  if (auth.state === "error") return <AuthError message={auth.message} />;

  // A `staff`-role user gets ONLY their self-view — the admin shell (sidebar,
  // every route, every section) NEVER mounts for them (staff-self-view AC1).
  // This is the front-end half of "completely inaccessible"; the API is the real
  // gate (every other endpoint 403s a staff token). Staff get a tiny router so
  // the roster is addressable at STAFF_PATH (anything else redirects there);
  // admins get the full shell. One BrowserRouter wraps both.
  return (
    <BrowserRouter>
      {auth.me.role === "customer" ? (
        // A CUSTOMER NEVER REACHES THE DESK SHELL. Every desk API gate already
        // refuses the role, and `visibleNav` hands it no navigation — so
        // mounting AppShell would draw an empty frame around a 403. They get
        // their own app, at their own URL, whatever they typed.
        <Routes>
          <Route path={CUSTOMER_HOME} element={<CustomerHome />} />
          <Route path={CUSTOMER_REGISTER} element={<CustomerHome />} />
          <Route path={CUSTOMER_BOOK} element={<CustomerHome />} />
          <Route path="/info" element={<CustomerHome />} />
          {/* The pay sheet over Home (deposit-proof AC1): its own URL, its stay in the path. */}
          <Route path={`${CUSTOMER_PAY}/:id`} element={<CustomerHome />} />
          {/* The dogs drawer over Home (vaccination-cards AC1), the pay sheet's way. */}
          <Route path={CUSTOMER_DOGS} element={<CustomerHome />} />
          <Route path="*" element={<Navigate to={CUSTOMER_HOME} replace />} />
        </Routes>
      ) : auth.me.role === "staff" ? (
        <Routes>
          <Route path={STAFF_PATH} element={<StaffView email={auth.me.email} />} />
          <Route path="*" element={<Navigate to={STAFF_PATH} replace />} />
        </Routes>
      ) : (
        <AppShell me={auth.me} />
      )}
    </BrowserRouter>
  );
}

// Provider brand marks — one copy of each official SVG, rendered inside the
// shared button shell so the markup can't drift between surfaces.
function GoogleMark() {
  return (
    <svg width="15" height="15" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}
function MicrosoftMark() {
  return (
    <svg width="15" height="15" viewBox="0 0 18 18" aria-hidden="true">
      <rect x="1" y="1" width="7.5" height="7.5" fill="#F25022" />
      <rect x="9.5" y="1" width="7.5" height="7.5" fill="#7FBA00" />
      <rect x="1" y="9.5" width="7.5" height="7.5" fill="#00A4EF" />
      <rect x="9.5" y="9.5" width="7.5" height="7.5" fill="#FFB900" />
    </svg>
  );
}

// One sign-in button shell, brand-marked per provider. Google is the primary
// (filled) action — most staff use it; Microsoft is the secondary (outline)
// alternate for Hotmail/Outlook accounts. The hierarchy is carried entirely by
// the modifier class (see the .signin-btn block in app.css), and it renders as a
// real <a> so it works with JS disabled.
function SigninButton({
  provider,
  label,
  href,
}: {
  provider: "google" | "microsoft";
  label: string;
  href: string;
}) {
  return (
    <a className={`signin-btn signin-btn--${provider}`} href={href}>
      <span className="signin-btn__logo">
        {provider === "google" ? <GoogleMark /> : <MicrosoftMark />}
      </span>
      <span className="signin-btn__label">{label}</span>
    </a>
  );
}

// The shared sign-in card scaffold — brand mark, title, then per-surface copy
// and the two sign-in buttons split by an "or" divider. The signed-out landing
// and the sign-in-error page are the same card with different words.
function SigninCard({ eyebrow, blurb, action, foot }: { eyebrow: string; blurb: string; action: string; foot: string }) {
  return (
    <main className="signin-landing">
      <div className="signin-card">
        <div className="signin-mark">
          <BrandMark size={56} />
        </div>
        <h1 className="signin-title">Beeches Brook</h1>
        <p className="signin-eyebrow">{eyebrow}</p>
        <p className="signin-blurb">{blurb}</p>
        <div className="signin-actions">
          <SigninButton provider="google" label={action} href={LOGIN_PATH} />
          <div className="signin-or" aria-hidden="true"><span>or</span></div>
          <SigninButton provider="microsoft" label="Sign in with Microsoft" href={MICROSOFT_LOGIN_PATH} />
        </div>
        <p className="signin-foot">{foot}</p>
      </div>
    </main>
  );
}

function SignedOut() {
  return (
    <SigninCard
      eyebrow="Boarding Kennels"
      blurb="Family-run boarding — a proper home away from home for your dog."
      action="Sign in with Google"
      foot="Sign in with your kennel Google or Microsoft account"
    />
  );
}

function AuthError({ message }: { message: string }) {
  return (
    <main style={{ fontFamily: "var(--font-sans)", padding: 24 }}>
      <h1>Beeches Brook</h1>
      <p>Could not check your session: {message}</p>
      <p>
        <a href="/">Try again</a>
      </p>
    </main>
  );
}

function DeniedPage() {
  return (
    <main style={{ fontFamily: "var(--font-sans)", padding: 24 }}>
      <h1>Access denied</h1>
      <p>
        Your account is not on the allowlist for this kennel. If you think this
        is a mistake, ask the owner to add you.
      </p>
      <p>
        <a href="/">Back to start</a>
      </p>
    </main>
  );
}

// Shown when the OAuth callback couldn't complete for a state reason (cookie not
// returned, or the login timed out / was interrupted) — reached after the server
// already tried one silent retry. Gives a clear, recoverable "Try again" instead
// of a raw JSON dead-end. `reason=cookies` gets a cookies-specific hint.
function SigninError() {
  const reason = new URLSearchParams(window.location.search).get("reason");
  const blurb =
    reason === "cookies"
      ? "Your browser didn't send the sign-in cookie back. Check that cookies are enabled, then try again — and start from this page rather than an old link."
      : "That sign-in didn't finish — it may have timed out or been interrupted. Please try again.";
  return (
    <SigninCard
      eyebrow="Sign-in didn't complete"
      blurb={blurb}
      action="Try again"
      foot="Still stuck? Ask the owner to check your access."
    />
  );
}
